<template>
	<!-- switch to v-model for visibility and keep reactivity via toRefs -->
	<Dialog v-model:visible="visible" class="w-full" :modal="props.modal" :closable="props.isClosable"
		v-bind="nonEventAttrs" @hide="onHide">
		<component v-if="props.innerComponent" :is="props.innerComponent" v-bind="props.innerProps" v-on="eventAttrs"
			@close="onClose" />
		<slot v-else v-bind="props.innerProps" v-on="eventAttrs" @close="onClose" />
	</Dialog>
</template>

<script setup lang="ts" generic="T extends Component">

	import type { Component } from 'vue';
	import { toRefs, useAttrs, computed } from 'vue';
	// `PropsOf` is defined in a shared utility file so it can be reused
	// throughout the frontend. Importing the type here keeps the component
	// lean and centralizes the logic for extracting props from a Vue
	// component.
	import type { PropsWithListenersOf } from '~/utils/vue';

	/* R3_MODAL_OWN_EMITS_START */
	type R3ModalOwnEmits = {
		/** Emitido quando o modal é fechado — permite usar `v-model:visible` */
		'update:visible': [value: boolean];
		/** Emitido ao ocultar o modal (compatibilidade com consumidores legados) */
		'hide': [];
		/** Solicitação de fechamento originada pelo componente interno */
		'close': [];
	};
	/* R3_MODAL_OWN_EMITS_END */

	const props = withDefaults(
		defineProps<{
			/** Componente a ser renderizado dentro do modal */
			innerComponent?: T | null;
			/**
			 * Props a serem repassadas ao componente interno.
			 * Os tipos são derivados automaticamente do componente informado em `innerComponent`,
			 * fornecendo IntelliSense completo para os props do componente interno.
			 *
			 * @remarks
			 * Inclui também listeners `onXxx` para permitir fallback de eventos
			 * como propriedades quando o LSP não infere `@evento` no wrapper.
			 */
			innerProps?: PropsWithListenersOf<T>;
			/** Define se o Dialog deve bloquear o restante da UI (padrão: true) */
			modal?: boolean;
			/**
			 * Define se haverá o botão nativo de fechar o dialog (padrão: false).
			 */
			isClosable?: boolean;
			/**
			 * Controla a visibilidade do modal.
			 * Suporta `v-model:visible` ou binding unidirecional `:visible`.
			 */
			visible?: boolean;
		}>(),
		{
			innerComponent: null as any,
			innerProps: () => ({}) as any,
			modal: true,
			isClosable: false,
			visible: false,
		},
	);

	// ensure reactivity for template bindings
	const { visible } = toRefs(props);

	// split attributes into event listeners and other props so we can
	// forward events to the inner component while still passing
	// non-listener attrs to the Dialog wrapper.
	const attrs = useAttrs();
	const ownEventListenerKeys = new Set(['onUpdate:visible', 'onHide', 'onClose']);
	const eventAttrs = computed(() => {
		const out: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(attrs)) {
			if (key.startsWith('on') && !ownEventListenerKeys.has(key)) {
				out[key] = value;
			}
		}
		return out;
	});
	const nonEventAttrs = computed(() => {
		const out: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(attrs)) {
			if (!key.startsWith('on')) {
				out[key] = value;
			}
		}
		return out;
	});



	// O modal expõe seus próprios eventos de controle (visibilidade/fechamento)
	// e, para casos específicos, eventos de componentes internos podem ser
	// adicionados manualmente na lista abaixo (ex: `PROC_INFORMA_CODIGO_USU_SEP`).
	const emit = defineEmits<R3ModalOwnEmits>();

	/**
	 * Chamado quando o `Dialog` interno dispara `@hide`.
	 * Emite `update:visible` para que o pai possa fechar o modal via v-model,
	 * além de manter o emit legado `hide`.
	 *
	 * @remarks
	 * A propriedade `visible` agora funciona como um `v-model:visible`
	 * bidirecional. Consumidores podem tanto fazer `:visible="foo"` quanto
	 * `v-model:visible="foo"`. O componente internamente usa a prop reativa
	 * via `toRefs` para não perder updates quando o pai altera o valor.
	 */
	function onHide() {
		(emit as any)('update:visible', false);
		(emit as any)('hide');

	}

	/**
	 * Handler chamado quando o conteúdo interno emite um evento `close`.
	 * Fecha o modal e repassa o evento para o pai.
	 */
	function onClose() {
		(emit as any)('update:visible', false);
		(emit as any)('close');
	}
</script>

<style lang="css" scoped>
	:global(.r3-modal-motion.p-dialog-enter-active) {
		animation: r3-modal-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
	}

	:global(.r3-modal-motion.p-dialog-leave-active) {
		animation: r3-modal-out 180ms cubic-bezier(0.4, 0, 1, 1);
	}

	:global(.r3-modal-motion .p-dialog-content) {
		will-change: transform, opacity;
	}

	@keyframes r3-modal-in {
		from {
			opacity: 0;
			transform: translateY(14px) scale(0.98);
		}

		to {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	@keyframes r3-modal-out {
		from {
			opacity: 1;
			transform: translateY(0) scale(1);
		}

		to {
			opacity: 0;
			transform: translateY(10px) scale(0.985);
		}
	}

	@media (prefers-reduced-motion: reduce) {

		:global(.r3-modal-motion.p-dialog-enter-active),
		:global(.r3-modal-motion.p-dialog-leave-active) {
			animation: none;
		}
	}
</style>
